import React, { useEffect, useMemo, useState } from "react";
import { Button } from "antd";
import Icon from "@mdi/react";
import { mdiCheck, mdiMagnify } from "@mdi/js";
import { PinIcon } from "../search-icons";
import type { Country, City } from "../../../../interfaces/country.interface";

export interface DestinationSelection {
  keyword: string;
  locationId: string | null;
}

interface Props {
  countries: Country[];
  selected: DestinationSelection;
  isMobile: boolean;
  onApply: (selection: DestinationSelection) => void;
}

const COLLAPSED_DESKTOP = 12;
const COLLAPSED_MOBILE = 5;

export default function DestinationPicker({ countries, selected, isMobile, onApply }: Props) {
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<DestinationSelection>(selected);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => setDraft(selected), [selected.keyword, selected.locationId]);

  const collapsedCount = isMobile ? COLLAPSED_MOBILE : COLLAPSED_DESKTOP;

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return countries;

    return countries
      .map((country) => {
        if (country.name.toLowerCase().includes(needle)) return country;
        const cities = (country.cities || []).filter((city) => city.name.toLowerCase().includes(needle));
        return cities.length ? { ...country, cities } : null;
      })
      .filter((country): country is Country => country !== null);
  }, [countries, query]);

  const selectCountry = (country: Country) =>
    setDraft(
      draft.keyword === country.name && !draft.locationId
        ? { keyword: "", locationId: null }
        : { keyword: country.name, locationId: null }
    );

  const selectCity = (city: City) =>
    setDraft(
      draft.locationId === String(city.id)
        ? { keyword: "", locationId: null }
        : { keyword: city.name, locationId: String(city.id) }
    );

  return (
    <div className="v2-destinations">
      <div className="v2-destinations__search">
        {isMobile ? <PinIcon size={20} /> : <Icon path={mdiMagnify} size="20px" />}
        <input
          value={query}
          placeholder={isMobile ? "Search" : "Search city, area"}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      <div className="v2-destinations__list">
        {matches.map((country) => {
          const cities = country.cities || [];
          const key = String(country.id);
          const isExpanded = expanded[key] || Boolean(query.trim());
          const visible = isExpanded ? cities : cities.slice(0, collapsedCount);
          const allSelected = draft.keyword === country.name && !draft.locationId;

          return (
            <section className="v2-destinations__group" key={key}>
              <header className="v2-destinations__header">
                <button
                  type="button"
                  className={`v2-destinations__all ${allSelected ? "is-active" : ""}`}
                  aria-pressed={allSelected}
                  onClick={() => selectCountry(country)}
                >
                  <span className="v2-destinations__box" aria-hidden="true">
                    {allSelected && <Icon path={mdiCheck} size="14px" />}
                  </span>
                  <h4>{country.name}</h4>
                  <span className="v2-destinations__all-label">Select all</span>
                </button>
              </header>

              {visible.length > 0 && (
                <div className="v2-destinations__chips">
                  {visible.map((city) => (
                    <button
                      key={city.id}
                      type="button"
                      className={`v2-region-chip ${
                        draft.locationId === String(city.id) || allSelected ? "is-active" : ""
                      }`}
                      onClick={() => selectCity(city)}
                    >
                      {city.name}
                    </button>
                  ))}
                </div>
              )}

              {cities.length > collapsedCount && !query.trim() && (
                <button
                  type="button"
                  className="v2-destinations__toggle"
                  onClick={() => setExpanded({ ...expanded, [key]: !isExpanded })}
                >
                  {isExpanded ? "See less" : "See more"}
                </button>
              )}
            </section>
          );
        })}

        {matches.length === 0 && <div className="v2-destinations__empty">No destinations found.</div>}
      </div>

      <div className="v2-filters__footer v2-destinations__footer">
        <Button
          type="text"
          className="v2-filters__clear"
          onClick={() => setDraft({ keyword: "", locationId: null })}
        >
          {isMobile ? "Clear All" : "Clear"}
        </Button>
        <Button type="primary" className="v2-filters__apply" onClick={() => onApply(draft)}>
          {isMobile ? "Apply" : "Show Results"}
        </Button>
      </div>
    </div>
  );
}
